import { CategoryKey, Money, usd } from "./types";

/**
 * Real negotiation projects from the WPC Demo org (id 106), IT vendors only.
 *
 * This is a frozen snapshot copied from production — TCV, identified savings and
 * score are the real values shown on each deal's negotiation playbook. The
 * `projectId` is the production project id; the demo links to the live project
 * page at `/project/:projectId`.
 */
export interface WpcProject {
    /** Production project id — used to deep-link to `/project/:projectId`. */
    projectId: number;
    name: string;
    vendorName: string;
    logoUrl?: string;
    categoryKey?: CategoryKey;
    category: string;
    /** Total contract value over the term. */
    tcv: Money;
    /** TCV annualised over the term — the figure the budget plans against. */
    annual: Money;
    /** Identified-savings range + expected (mid) — drives the playbook. */
    savingsLow: Money;
    savingsExpected: Money;
    savingsHigh: Money;
    /** Deal score, 0–100 (the order form's score). */
    score: number;
    startDate: string;
    endDate?: string;
    autoRenew: boolean;
}

const LOGO = (domain: string) =>
    `https://img.logo.dev/${domain}?token=pk_DmFwLlBkSAyEWWyE_dUI2Q&retina=true`;

// Real values from the WPC Demo account — shown as-is so they match each deal's
// production project page and playbook.
export const WPC_PROJECTS: WpcProject[] = [
    {
        projectId: 1055,
        name: "AWS",
        vendorName: "Amazon Web Services",
        logoUrl: LOGO("aws.com"),
        categoryKey: "infra_cloud",
        category: "Cloud Services",
        tcv: usd(173_000_000),
        annual: usd(57_666_667),
        savingsLow: usd(13_390_000),
        savingsExpected: usd(17_930_000),
        savingsHigh: usd(31_000_000),
        score: 55,
        startDate: "2026-01-01",
        endDate: "2028-12-31",
        autoRenew: false,
    },
    {
        projectId: 1064,
        name: "Enterprise UCaaS",
        vendorName: "Zoom",
        logoUrl: LOGO("zoom.com"),
        categoryKey: "infra_cloud",
        category: "Cloud Services",
        tcv: usd(23_999_879),
        annual: usd(7_999_959),
        savingsLow: usd(1_420_000),
        savingsExpected: usd(1_555_000),
        savingsHigh: usd(2_650_000),
        score: 45,
        startDate: "2024-06-01",
        endDate: "2027-06-01",
        autoRenew: false,
    },
    {
        projectId: 1056,
        name: "ETLA Renewal",
        vendorName: "Adobe",
        logoUrl: LOGO("adobe.com"),
        categoryKey: "saas",
        category: "Content Creation",
        tcv: usd(1_595_359),
        annual: usd(531_786),
        savingsLow: usd(129_000),
        savingsExpected: usd(252_000),
        savingsHigh: usd(375_000),
        score: 65,
        startDate: "2026-05-01",
        endDate: "2029-05-01",
        autoRenew: false,
    },
    {
        projectId: 1061,
        name: "Harmonize Platform",
        vendorName: "Optimized Payments",
        logoUrl: LOGO("optimizedpayments.com"),
        categoryKey: "saas",
        category: "Financial Consulting",
        tcv: usd(270_000),
        annual: usd(90_000),
        savingsLow: usd(36_000),
        savingsExpected: usd(45_000),
        savingsHigh: usd(54_000),
        score: 80,
        startDate: "2026-05-01",
        endDate: "2029-04-01",
        autoRenew: false,
    },
];

/** In-app SPA route to a project page (navigated within the current app, not a
 * redirect to production). */
export const projectUrl = (projectId: number): string => `/project/${projectId}`;
export const projectPath = (p: WpcProject): string => projectUrl(p.projectId);
